import { z } from 'zod';
import {
  conversationMessageSchema,
  conversationPositionSchema,
  type ChatMessage,
} from './chat-response';

export const CONVERSATION_HISTORY_DEFAULT_LIMIT = 50;
export const CONVERSATION_HISTORY_MAX_LIMIT = 100;

export const conversationHistoryQuerySchema = z
  .object({
    conversationId: z.string().uuid(),
    beforePosition: conversationPositionSchema.optional(),
    afterPosition: conversationPositionSchema.optional(),
    limit: z
      .number()
      .int()
      .min(1)
      .max(CONVERSATION_HISTORY_MAX_LIMIT)
      .default(CONVERSATION_HISTORY_DEFAULT_LIMIT),
  })
  .strict()
  .refine((value) => value.beforePosition === undefined || value.afterPosition === undefined, {
    message: 'beforePosition and afterPosition are mutually exclusive',
  });

export type ConversationHistoryQuery = z.infer<typeof conversationHistoryQuerySchema>;

export const conversationHistoryPageSchema = z
  .object({
    messages: z.array(conversationMessageSchema).max(CONVERSATION_HISTORY_MAX_LIMIT),
    hasOlder: z.boolean(),
    oldestPosition: conversationPositionSchema.nullable(),
    newestPosition: conversationPositionSchema.nullable(),
  })
  .strict();

export type ConversationHistoryPage = z.infer<typeof conversationHistoryPageSchema>;

export interface ConversationVisibilityInterval {
  visibleFromPosition: number;
  visibleThroughPosition: number | null;
}

/** Pure reference projection used by contract tests; the API repository owns the SQL query. */
export function projectConversationHistory(
  source: readonly ChatMessage[],
  query: ConversationHistoryQuery,
  visibility: ConversationVisibilityInterval,
): ConversationHistoryPage {
  const parsed = conversationHistoryQuerySchema.parse(query);
  const visible = source
    .filter(
      (message) =>
        message.conversationId === parsed.conversationId &&
        message.position >= visibility.visibleFromPosition &&
        (visibility.visibleThroughPosition === null ||
          message.position <= visibility.visibleThroughPosition),
    )
    .sort((left, right) => left.position - right.position);

  const candidates = visible.filter((message) => {
    if (parsed.beforePosition !== undefined) return message.position < parsed.beforePosition;
    if (parsed.afterPosition !== undefined) return message.position > parsed.afterPosition;
    return true;
  });

  const selected =
    parsed.afterPosition !== undefined
      ? candidates.slice(0, parsed.limit)
      : candidates.slice(Math.max(0, candidates.length - parsed.limit));
  const oldestPosition = selected[0]?.position ?? null;
  const newestPosition = selected.at(-1)?.position ?? null;

  return {
    messages: selected,
    hasOlder:
      oldestPosition !== null && visible.some((message) => message.position < oldestPosition),
    oldestPosition,
    newestPosition,
  };
}
